
import { throwError as observableThrowError, Observable, Observer } from 'rxjs';
import { Injectable } from '@angular/core';
import { Headers } from '@angular/http';
import { Account } from './account.service';
import { Config } from '../config';
import { HttpService } from './http.service';
import { OAuthToken } from '../models/oauth-token';
import { OAuthTokenRequest } from '../models/oauth-token-request';
import { Token } from '../models/token';

import { environment } from "../../environments/environment";
import { IdentityServiceMoc } from "./identity.service.moc";
import { UserRoles } from '../models/user_roles';
import { IdPUser } from '../models/idpuser';
import { IdPUserReply } from '../models/idpusers_reply';
import { FindIdPUser } from '../models/find_idpuser';
import { IdPUserTextSearch } from '../models/idpuser_text_search';
import { IdPUserTextSearchCount } from '../models/idpuser_text_search_count';
import { IdPUserRequest } from '../models/idpuser_request';
import { FindIdPUserRole } from '../models/find_idpuser_role';
import { IdPUserRole } from '../models/idpuser_role';
import { IdPUserRoleReply } from '../models/idpuser_role_reply';
import { FindIdP } from '../models/find_idp';
import { IdP } from '../models/idp';
import { IdPReply } from '../models/idp_reply';
import { IdPRequest } from '../models/idp_request';
import { IdentityUser } from '../models/identity_user';
import { InviteUser } from '../models/invite_user';
import { SAML2Config } from '../models/saml2_config';
import { CountAccountAssociation } from '../models/count_account_association';
import { FindAccountAssociation } from '../models/find_account_association';
import { AccountAssociation } from '../models/account_association';
import { IdPDomain } from '../models/idp_domain';
import { IdPDomainReply } from '../models/idp_domain_reply';
import { IdPDomainRequest } from '../models/idp_domain_request';
import { EndpointsReply } from '../models/test_urls_reply';
import { FindIdPRolesMapping } from '../models/find_idp_roles_mapping';
import { IdPRolesMapping } from '../models/idp_roles_mapping';
import { IdPRolesMappingReply } from '../models/idp_roles_mapping_reply';
import { IdPRolesMappingRequest } from '../models/idp_roles_mapping_request';
import { IdPRolesMappingTextSearch } from '../models/idp_roles_mapping_text_search';
import { IdPRolesMappingTextSearchCount } from '../models/idp_roles_mapping_search_count';

@Injectable()
export class IdentityService {
	userRolesKey: string = 'userRoles';
	timeout: number = 10000; //10 sec
	retry: number = 2; //max retry
	delay: number = 3000; //max delay after retry
	errors: any = {};

	constructor(private http: HttpService, private account: Account, private config: Config) {
		this.errors['default'] = 'There is an error when processing this request, please try again later.';
		this.errors['no connection'] = 'Your device seems to be offline, please try again later.'
		this.errors['unauthorized'] = 'Unauthorized access. Please try login again.';
		this.errors['not found'] = 'No results.';
		this.errors['invalid token'] = 'Unauthorized access. Please try login again.';
		this.errors['duplicate entry'] = 'This data already exists.';
		this.errors['bad request'] = 'Invalid request.';
		this.errors['internal server error'] = 'Internal server error. Please try again later.';
		this.errors['unexpected'] = 'Sorry, an unexpected error has occured. Please contact Tech Support.';
		this.errors['timeout'] = 'Timeout when communicating with a server, please try again later.';
		this.errors['email address is invalid'] = 'Email address is invalid.';
		this.errors['user status is invalid'] = 'Invalid user status.';
		this.errors['user status signup is invalid email not confirmed'] = 'The user you\'ve invited has not yet confirmed their email address.';
		this.errors['notification not supported'] = 'Push notifications are not supported in this browser.';
		this.errors['account license expired'] = 'Account License has expired. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['invalid account license'] = 'Account License is invalid. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['unauthorized access role'] = 'Unauthorized access role.';
		this.errors['identity license expired'] = 'Identity License has expired. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['invalid identity license'] = 'Identity License is invalid. Please visit the District Manager Subscription page for more details and license renewal.';
		this.errors['domain name not found'] = 'Domain name(s) not found.';
		this.errors['domain name not verified'] = 'Domain name(s) not verified.';
		this.errors['domain name denied'] = 'This domain is not allowed to be used as part of identity provider.';
		this.errors['username domain not match with idp domain name'] = 'Username domain name not maching any of IdP domain names.';
	}

	validateAcctNumber(acctNumber: string): Observable<boolean> {
		let lusr = this.userRoles();
		if (lusr != null && lusr.acctNumber == acctNumber) {
			//return non http observable when the userroles exists at localStorage
			return Observable.create((observer: Observer<boolean>) => {
				observer.next(true);
				observer.complete();
			});
		}
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(false);
			observer.complete();
		});
	}

	loadUser(): Observable<UserRoles> {
		return this.account.getUserProfileApi()
			.do(us => {
				this.account.setUser(us);
			})
			.mergeMap(() => {
				return this.userRolesApi()
					.map(urs => {
						this.setUserRoles(urs[0]);
						return urs[0]
					});
			});
	}

	loadUserByAcctNumber(acctNumber: string): Observable<UserRoles> {
		return this.account.getUserProfileApi()
			.do(us => {
				this.account.setUser(us);
			})
			.mergeMap(() => {
				return this.userRolesApi()
					.map(urs => {
						for (let u of urs) {
							if (u.acctNumber === acctNumber) {
								this.setUserRoles(u);
								return u;
							}
						}
						throw new Error('unauthorized');
					});
			});
	}

	removeLocalStorage() {
		localStorage.removeItem(this.userRolesKey);
	}

	setUserRoles(us: UserRoles) {
		localStorage.setItem(this.userRolesKey, JSON.stringify(us));
	}

	userRoles() {
		if (localStorage.getItem(this.userRolesKey) === null) {
			return null
		}
		return JSON.parse(localStorage.getItem(this.userRolesKey));
	}

	getToken() {
		let t = this.account.getToken();
		if (!t) {
			t = new Token();
		}
		return t;
	}

	getError(err: any): Error {
		var er: any;
		var msg = 'no connection';
		if (typeof (err.json) === 'function') {
			er = err.json();
			if (er.message != null && er.message != null && er.message != '') {
				msg = er.message;
			} else if (er.error != null && er.error != null && er.error != '') {
				msg = er.error;
			} else {
				console.log(err);
			}
			return new Error(msg);
		} else if (err instanceof Error) {
			return err;
		} else {
			console.log(err);
		}
	}

	getUserError(err: Error): string {
		return this.errors[err.message];
	}

	isErrNotFound(err: Error): boolean {
		return err.message == 'not found';
	}

	isErrUnauthorized(err: Error): boolean {
		return err.message == 'unauthorized' ||
			err.message == 'unauthorized access role';
	}

	getUserErrorDefault(err: Error): string {
		let e = this.errors[err.message];
		if (e != null) {
			return e;
		}
		return this.errors['default'];
	}

	buildUrl(parameters) {
		var qs = "";
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				qs += encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
			}
		}
		if (qs.length > 0) {
			return qs.substring(0, qs.length - 1); //chop off last "&"
		}
		return '';
	}

	params(parameters) {
		var params: any = {}
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				params[encodeURIComponent(key)] = encodeURIComponent(value);
			}
		}
		return params;
	}

	leftPad(val: string, size: number, ch: string): string {
		var result = String(val);
		if (!ch) {
			ch = " ";
		}
		while (result.length < size) {
			result = ch + result;
		}
		return result;
	}

	hexAcctNumber(num: number): string {
		//sign >>> is Zero-fill right shift
		return this.leftPad((num >>> 0).toString(16), 8, '0').toUpperCase();
	}


	isErrDuplicateEntry(err: Error): boolean {
		return err.message == 'duplicate entry';
	}

	isErrNoConnection(err: Error): boolean {
		return err.message == 'no connection';
	}

	isErrAccountLicenseExpired(err: Error): boolean {
		return err.message == 'account license expired';
	}

	isErrAccountLicenseInvalid(err: Error): boolean {
		return err.message == 'invalid account license';
	}

	getDefaultSaveMessage(): string {
		return "Successfully​ Saved";
	}

	containsUserRole(ur: UserRoles, role: string): boolean {
		for (var r in ur.roles) {
			if (ur.roles[r] == role) {
				return true;
			}
		}
		return false;
	}

	oauthTokenApi(req: OAuthTokenRequest): Observable<OAuthToken> {
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		let rq = JSON.stringify(req);

		// true at the end means skipping the http interception
		return this.http.post(`${this.config.identityApiUrl}/oauth2/token`, rq, { headers: headers }, true)
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// Identity User APIs
	userRolesApi(): Observable<UserRoles[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.get(this.config.identityApiUrl + '/identity/user/roles', { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var urs: UserRoles[] = [];
					for (var v in data) {
						urs.push({
							acctNumber: this.hexAcctNumber(data[v].acctNumber),
							schoolName: data[v].schoolName,
							isDefault: data[v].isDefault,
							roles: data[v].roles,
						});
					}
					return urs
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	updateUserApi(acctNumber: string, req: IdentityUser): Observable<boolean> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify({ roles: req.roles });
		return this.http.put(`${this.config.identityApiUrl}/identity/${acctNumber}/user/${req.userId}/role`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return true;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	usersApi(acctNumber: string): Observable<IdentityUser[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		var params: any = {};
		return this.http.get(`${this.config.identityApiUrl}/identity/${acctNumber}/users`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				var rows: IdentityUser[] = [];
				if (resp.status == 200) {
					var data = resp.json().list;
					for (var i in data) {
						let row = new IdentityUser();
						row.userId = data[i].userId;
						row.firstName = data[i].firstName;
						row.lastName = data[i].lastName;
						row.email = data[i].email;
						row.status = data[i].status;
						row.owner = data[i].owner;
						if (data[i].roles.length > 0) {
							for (var j in data[i].roles) {
								row.roles.push(data[i].roles[j]);
							}
						}
						rows.push(row)
					}
					return rows
				} else if (resp.status == 404) {
					console.log('404');
					return rows;
				}
				else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	inviteUserApi(acctNumber: string, req: InviteUser): Observable<IdentityUser> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		let rq = JSON.stringify({ email: req.email });
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/user/invite`, rq, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json()
					let row = new IdentityUser();
					row.userId = data.userId;
					row.firstName = data.firstName;
					row.lastName = data.lastName;
					row.email = data.email;
					row.status = data.status;
					row.roles = data.roles;
					row.owner = data.owner;

					return row;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	removeUserApi(acctNumber: string, req: IdentityUser): Observable<boolean> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.identityApiUrl}/identity/${acctNumber}/user/${req.userId}/role`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return true;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	setDefaultApi(req: UserRoles): Observable<boolean> {
		var token = this.getToken();
		var headers = new Headers();
		var acctNumber = req.acctNumber;
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.put(this.config.identityApiUrl + '/identity/' + acctNumber + '/user/role/default', req, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				if (resp.status == 200) {
					return true;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// IdP Users API
	findIdPUserApi(acctNumber: string, req: FindIdPUser): Observable<IdPUser[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(this.config.identityApiUrl + `/identity/${acctNumber}/idp/users/find`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var ids: IdPUser[] = [];
					for (var v in data) {
						ids.push({
							id: data[v].id,
							idpId:data[v].idpId,
							acctNumber: this.hexAcctNumber(data[v].acctNumber),
							username: data[v].username,
							firstName: data[v].firstName,
							lastName: data[v].lastName,
							cardId: data[v].cardId,
							phoneNumber: data[v].phoneNumber,
							groupNames: data[v].groupNames,
							idpName: data[v].idpName,
							userId: data[v].userId,
							createdAt: new Date(data[v].createdAt),
							updatedAt: new Date(data[v].updatedAt),
							lastLoginAt: new Date(data[v].lastLoginAt),
						});
					}
					return ids
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	removeIdPUsersApi(acctNumber: string, id: number): Observable<IdPUserReply>{
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/users/${id}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	removeAllIdPUsersApi(acctNumber: string, req: IdPUserTextSearch): Observable<void> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/users/remove-all`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status != 200) {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	textSearchIdPUsersApi(acctNumber: string, req: IdPUserTextSearch): Observable<IdPUser[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/users/search`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var ids: IdPUser[] = [];
					for (var v in data) {
						ids.push({
							id: data[v].id,
							idpId:data[v].idpId,
							acctNumber: this.hexAcctNumber(data[v].acctNumber),
							username: data[v].username,
							firstName: data[v].firstName,
							lastName: data[v].lastName,
							cardId: data[v].cardId,
							phoneNumber: data[v].phoneNumber,
							groupNames: data[v].groupNames,
							idpName: data[v].idpName,
							userId: data[v].userId,
							createdAt: new Date(data[v].createdAt),
							updatedAt: new Date(data[v].updatedAt),
							lastLoginAt: new Date(data[v].lastLoginAt),
						});
					}
					return ids
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	textSearchCountIdPUsersApi(acctNumber: string, req: IdPUserTextSearch): Observable<IdPUserTextSearchCount> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/users/search/count`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	updateIdPUsersApi(acctNumber: string, id: number, req: IdPUserRequest): Observable<IdPUserReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		// let rq = JSON.stringify(req);
		return this.http.put(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/users/${id}`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	addIdPUsersApi(acctNumber: string, req: IdPUserRequest): Observable<IdPUserReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/users`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// IdP APIs
	findIdPApi(acctNumber: string, req: FindIdP): Observable<IdP[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/find`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var idps: IdP[] = [];
					for (var v in data) {
						idps.push({
							id: data[v].id,
							domainNames: data[v].domainNames,
							name: data[v].name,
							protocol: data[v].protocol,
							userProvisioning: data[v].userProvisioning,
							createdAt: new Date(data[v].createdAt),
							updatedAt: new Date(data[v].updatedAt),
						});
					}
					return idps;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	removeIdPApi(acctNumber: string, id: number): Observable<IdPReply>{
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/${id}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	updateIdPApi(acctNumber: string, req: IdPRequest): Observable<IdPReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		// let rq = JSON.stringify(req);
		return this.http.put(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/${req.id}`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	addIdPApi(acctNumber: string, req: IdPRequest): Observable<IdPReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	saml2ConfigApi(acctNumber: string, req: FindIdP): Observable<SAML2Config> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/${req.id}/saml2/config`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	saveSaml2ConfigApi(acctNumber: string, idpId: number, req: SAML2Config): Observable<IdPReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/${idpId}/saml2/config/save`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	endpointsApi(acctNumber: string): Observable<EndpointsReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.get(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/endpoints`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// IDP UserRole APIs

	findIdPUserRoleApi(acctNumber: string, req: FindIdPUserRole): Observable<IdPUserRole[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/userrole/find`,req ,{ headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var ids: IdPUserRole[] = [];
					for (var v in data) {
						ids.push({
							id: data[v].id,
							idpId: data[v].idpId,
							userId: data[v].userId,
							orgAcctNumber: data[v].orgAcctNumber,
							serviceId: data[v].serviceId,
							roles: data[v].roles,
							updatedAt: new Date(data[v].updatedAt),
							organizationName: data[v].organizationName,
						});
					}
					return ids
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	removeIdPUserRoleApi(acctNumber: string, id: number): Observable<IdPUserRoleReply>{
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/userrole/${id}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	addIdPUserRoleApi(acctNumber: string,data: IdPUserRole): Observable<IdPUserRoleReply>{
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/userrole`, data,{ headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	updateIdPUserRolesApi(acctNumber: string, id: number,data: IdPUserRole): Observable<IdPUserRoleReply>{
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.put(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/userrole/${id}`,data, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}
	// Account Association
	findAccountAssociation(acctNumber: string, req: FindAccountAssociation): Observable<AccountAssociation[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/account-association/find`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var ids: AccountAssociation[] = [];
					for (var v in data) {
						ids.push({
							acctNumber: data[v].acctNumber,
							parentAccountNumber:this.hexAcctNumber(data[v].parentAccountNumber),
							organizationName: data[v].organizationName,
							state: data[v].state,
							zipCode: data[v].zipCode,
							joinedAt: data[v].joinedAt,
							enabled: data[v].enabled,
							createdAt: data[v].createdAt,
							updatedAt: data[v].updatedAt,
						});
					}
					return ids
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}
	findCountAccountAssociation(acctNumber: string, req: FindAccountAssociation): Observable<CountAccountAssociation> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.get(`${this.config.identityApiUrl}/identity/${acctNumber}/account-association/count`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// IdP Domain
	findIdPDomainApi(acctNumber: string): Observable<IdPDomain[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/domains/find`,{ headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var dns: IdPDomain[] = [];
					for (var v in data) {
						dns.push({
							id: data[v].id,
							domainName: data[v].domainName,
							status: data[v].status,
							createdAt: new Date(data[v].createdAt),
						});
					}
					return dns;
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	removeIdPDomainApi(acctNumber: string, id: number): Observable<IdPDomainReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.identityApiUrl}/identity/${acctNumber}/domains/${id}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	resendDomainConfirmationApi(acctNumber: string, id: number): Observable<IdPDomainReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/domains/${id}/confirmation/resend`, { headers: headers })
			.timeout(this.timeout)
			.retryWhen(error => this.httpRetry(error))
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	addIdPDomainApi(acctNumber: string, req: IdPDomainRequest): Observable<IdPDomainReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/domains`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	verifyIdPDomainApi(acctNumber: string, verifyToken: string): Observable<IdPDomainReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/domains/verify?token=${verifyToken}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	// IdP Roles Mapping
	findIdPRolesMappingApi(acctNumber: string, req: FindIdPRolesMapping): Observable<IdPRolesMapping[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(this.config.identityApiUrl + `/identity/${acctNumber}/idp/roles-mapping/find`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var ids: IdPRolesMapping[] = [];
					for (var v in data) {
						ids.push({
							id: data[v].id,
							idpId: data[v].idpId,
							accountGroups: data[v].accountGroups,
							orgAcctNumber: data[v].orgAcctNumber,
							orgName: data[v].orgName,
							serviceId: data[v].serviceId,
							serviceRoles: data[v].serviceRoles,
							serviceRolesGroups: data[v].serviceRolesGroups,
							idpName: data[v].idpName,
							createdAt: new Date(data[v].createdAt),
							updatedAt: new Date(data[v].updatedAt),
						});
					}
					return ids
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	removeIdPRolesMappingApi(acctNumber: string, id: number): Observable<IdPRolesMappingReply>{
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.delete(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/roles-mapping/${id}`, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	updateIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingRequest): Observable<IdPRolesMappingReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.put(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/roles-mapping/${req.id}`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	addIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingRequest): Observable<IdPRolesMappingReply> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/roles-mapping`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	textSearchIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingTextSearch): Observable<IdPRolesMapping[]> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(this.config.identityApiUrl + `/identity/${acctNumber}/idp/roles-mapping/search`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					var data = resp.json().list;
					var ids: IdPRolesMapping[] = [];
					for (var v in data) {
						ids.push({
							id: data[v].id,
							idpId: data[v].idpId,
							accountGroups: data[v].accountGroups,
							orgAcctNumber: data[v].orgAcctNumber,
							orgName: data[v].orgName,
							serviceId: data[v].serviceId,
							serviceRoles: data[v].serviceRoles,
							serviceRolesGroups: data[v].serviceRolesGroups,
							idpName: data[v].idpName,
							createdAt: new Date(data[v].createdAt),
							updatedAt: new Date(data[v].updatedAt),
						});
					}
					return ids
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	textSearchCountIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingTextSearch): Observable<IdPRolesMappingTextSearchCount> {
		var token = this.getToken();
		var headers = new Headers();
		headers.append('Content-Type', 'application/json');
		headers.append('Authorization', token.tokenType + ' ' + token.accessToken);
		return this.http.post(`${this.config.identityApiUrl}/identity/${acctNumber}/idp/roles-mapping/search/count`, req, { headers: headers })
			.timeout(this.timeout)
			.map(resp => {
				if (resp.status == 200) {
					return resp.json()
				} else {
					throw this.getError(resp);
				}
			})
			.catch((err) => {
				let er = this.getError(err);
				return observableThrowError(er);
			});
	}

	httpRetry(o: Observable<any>): Observable<any> {
		return o
			.mergeMap((error, count) => {
				if (count < this.retry) {
					return Observable.of(error);
				}
				return observableThrowError(this.getError(error));
			})
			.delay(this.delay);
	}
}

export function IdentityServiceFactory(
	http: HttpService,
	account: Account,
	config: Config
) {
	return (environment.isMoc) ? new IdentityServiceMoc(http, account, config) : new IdentityService(http, account, config);
}
